import React, { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Upload, X, Package, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import ImagePreview from './ImagePreview';
import { CompressionConfig } from './CompressionSettings';
import { useImageFiles } from '@/hooks/useImageFiles';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { downloadFilesAsZip } from '@/utils/imageCompression';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsMobile } from '@/hooks/use-mobile';

export interface ImageUploaderHandle {
  addAndCompressFiles: (files: File[]) => void;
}

const ImageUploader = forwardRef<ImageUploaderHandle>((_, ref) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [autoCompressQueued, setAutoCompressQueued] = useState(false);
  const [compressionConfig, setCompressionConfig] = useState<CompressionConfig>({
    quality: 0.7,
    preset: 'medium',
    autoResize: false,
    maxWidth: undefined,
    maxHeight: undefined
  });
  const { toast } = useToast();
  const { isPremium } = useSubscription();
  const { trackEvent } = useAnalytics();
  const isMobile = useIsMobile();
  const hasTrackedVisibility = useRef(false);
  const compressionStartTime = useRef<number>(0);
  const uploaderSectionRef = useRef<HTMLDivElement>(null);
  const prevCompletedCount = useRef(0);

  const { 
    filesData, 
    addFiles, 
    removeFile, 
    clearFiles, 
    compressFile,
    compressAllFiles,
    retryFile,
    getFailedFiles,
    getCompletedFiles,
    canAddFiles,
    getRemainingSlots,
    maxFiles,
    maxRetryAttempts,
    usageData
  } = useImageFiles(isPremium);

  const usageDataRef = useRef(usageData);
  usageDataRef.current = usageData;
  const compressionConfigRef = useRef(compressionConfig);
  compressionConfigRef.current = compressionConfig;

  // IntersectionObserver for uploader_visible
  useEffect(() => {
    const el = uploaderSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasTrackedVisibility.current) {
        trackEvent('uploader_visible', { device_type: isMobile ? 'mobile' : 'desktop' });
        hasTrackedVisibility.current = true;
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [trackEvent, isMobile]);

  // Auto-compress pending files when queued
  useEffect(() => {
    if (autoCompressQueued) {
      const pendingFiles = filesData.filter(f => f.status === 'pending');
      const processingFiles = filesData.filter(f => f.isCompressing);
      if (pendingFiles.length > 0 && processingFiles.length === 0) {
        setAutoCompressQueued(false);
        compressionStartTime.current = Date.now();
        trackEvent('compression_start', { count: pendingFiles.length });
        compressAllFiles(compressionConfigRef.current);
        trackEvent('compress', { count: pendingFiles.length, quality: compressionConfigRef.current.quality, auto: true });
      }
    }
  }, [autoCompressQueued, filesData, compressAllFiles, trackEvent]);

  // Track compression_success when new files complete
  useEffect(() => {
    const currentCompleted = getCompletedFiles();
    if (currentCompleted.length > prevCompletedCount.current && prevCompletedCount.current >= 0) {
      const newCompleted = currentCompleted.slice(prevCompletedCount.current);
      newCompleted.forEach(f => {
        if (f.compression) {
          const originalKb = Math.round(f.compression.originalSize / 1024);
          const compressedKb = Math.round(f.compression.compressedSize / 1024);
          const reductionPercent = Math.round(f.compression.compressionRatio);
          const processingMs = compressionStartTime.current ? Date.now() - compressionStartTime.current : 0;
          trackEvent('compression_success', {
            original_size_kb: originalKb,
            compressed_size_kb: compressedKb,
            reduction_percent: reductionPercent,
            processing_time_ms: processingMs,
          });
        }
      });
    }
    prevCompletedCount.current = currentCompleted.length;
  }, [filesData, getCompletedFiles, trackEvent]);

  const validateFile = (file: File) => {
    if (!file.type.startsWith('image/jpeg') && 
        !file.type.startsWith('image/jpg') && 
        !file.type.startsWith('image/png') && 
        !file.type.startsWith('image/webp')) {
      return false;
    }
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      return false;
    }
    return true;
  };

  const handleFiles = useCallback(async (fileList: FileList | File[]) => {
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];
    const totalFiles = Array.from(fileList);

    const remainingSlots = getRemainingSlots();
    if (remainingSlots === 0) {
      toast({
        title: "Límite alcanzado",
        description: `Ya tienes muchas imágenes. Elimina algunas para agregar más.`,
        variant: "destructive",
      });
      return;
    }

    totalFiles.forEach(file => {
      if (validateFile(file)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    const filesToAdd = remainingSlots === Infinity ? 
      validFiles : 
      validFiles.slice(0, remainingSlots);
    const exceedingFiles = validFiles.length - filesToAdd.length;

    if (filesToAdd.length > 0) {
      await addFiles(filesToAdd);
      trackEvent('upload', { count: filesToAdd.length });
      filesToAdd.forEach(f => {
        trackEvent('upload_success', {
          file_type: f.type,
          file_size_kb: Math.round(f.size / 1024),
          images_count: filesToAdd.length,
        });
      });
      // Queue auto-compression
      setAutoCompressQueued(true);
    }

    if (exceedingFiles > 0) {
      toast({
        title: "Límite de archivos",
        description: `${exceedingFiles} archivo(s) no se pudieron agregar.`,
        variant: "destructive",
      });
    }

    if (invalidFiles.length > 0) {
      const supportedFormats = "JPEG, PNG, WebP";
      toast({
        title: "Archivos no válidos",
        description: `${invalidFiles.length} archivo(s) no son ${supportedFormats} válidos o exceden 50MB.`,
        variant: "destructive",
      });
    }
  }, [addFiles, toast, getRemainingSlots, maxFiles, trackEvent]);

  // Expose ref for external components (e.g. ExampleImages)
  useImperativeHandle(ref, () => ({
    addAndCompressFiles: (files: File[]) => {
      trackEvent('upload_click', { method: 'example_image' });
      handleFiles(files);
    }
  }), [handleFiles, trackEvent]);

  const handleCompressAll = async () => {
    const pendingFiles = filesData.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) {
      toast({
        title: "Sin archivos pendientes",
        description: "No hay imágenes pendientes para comprimir.",
        variant: "destructive",
      });
      return;
    }

    await compressAllFiles(compressionConfig);
    trackEvent('compress', { count: pendingFiles.length, quality: compressionConfig.quality });
  };

  const downloadAll = async () => {
    const compressedFiles = getCompletedFiles().filter(fileData => fileData.compression);
    
    if (compressedFiles.length === 0) {
      toast({
        title: "Sin archivos comprimidos",
        description: "No hay imágenes comprimidas para descargar.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsDownloadingAll(true);
      const filesToDownload = compressedFiles.map(fileData => fileData.compression!.compressedFile);
      const zipName = `imagenes_comprimidas_${new Date().toISOString().split('T')[0]}.zip`;
      
      await downloadFilesAsZip(filesToDownload, zipName);
      trackEvent('download', { count: compressedFiles.length });
      
      toast({
        title: "Descarga completada",
        description: `${compressedFiles.length} imagen(es) descargada(s) en ${zipName}.`,
      });
    } catch (error) {
      console.error('Error al descargar archivos:', error);
      toast({
        title: "Error en la descarga",
        description: "No se pudo crear el archivo ZIP. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      trackEvent('upload_click', { method: 'drag_drop' });
      handleFiles(droppedFiles);
    }
  }, [handleFiles, trackEvent]);

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      trackEvent('upload_click', { method: 'button' });
      handleFiles(selectedFiles);
    }
  }, [handleFiles, trackEvent]);

  const failedFiles = getFailedFiles();
  const completedFiles = getCompletedFiles();
  const pendingFiles = filesData.filter(f => f.status === 'pending');
  const remainingSlots = getRemainingSlots();
  const processingFiles = filesData.filter(f => f.isCompressing);
  const maxSizeText = "50MB";
  
  const maxFilesNumeric = typeof maxFiles === 'number' ? maxFiles : null;
  const currentFileCount = filesData.length;

  const isUploadDisabled = remainingSlots === 0;

  // Calculate compression progress
  const totalFilesCount = filesData.length;
  const processedCount = completedFiles.length + failedFiles.length;
  const compressionProgress = totalFilesCount > 0 ? (processedCount / totalFilesCount) * 100 : 0;
  const isProcessing = processingFiles.length > 0;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Large Dropzone - Primary Element */}
      <div
        id="uploader-section"
        ref={uploaderSectionRef}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={`
          relative border-2 border-dashed rounded-2xl text-center transition-all duration-300 cursor-pointer
          min-h-[280px] md:min-h-[400px] flex items-center justify-center
          ${isDragActive 
            ? 'border-primary bg-primary/5 scale-[1.02]' 
            : isUploadDisabled
            ? 'border-border bg-muted cursor-not-allowed'
            : 'border-primary/40 border-[3px] bg-card hover:border-primary/60 hover:bg-accent/50'
          }
        `}
      >
        <input
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={onInputChange}
          disabled={isUploadDisabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />
        
        <div className="space-y-4 px-6 py-8">
          <div className={`mx-auto w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-colors duration-300 ${
            isDragActive ? 'bg-primary' : isUploadDisabled ? 'bg-muted' : 'bg-primary/10'
          }`}>
            <Upload className={`w-8 h-8 md:w-10 md:h-10 ${
              isDragActive ? 'text-primary-foreground' : isUploadDisabled ? 'text-muted-foreground' : 'text-primary'
            }`} />
          </div>
          
          <div className="space-y-3">
            {isUploadDisabled ? (
              <>
                <h3 className="text-xl md:text-2xl font-semibold text-muted-foreground">
                  Límite de imágenes alcanzado
                </h3>
                <p className="text-base text-muted-foreground">
                  Elimina algunas imágenes para agregar más
                </p>
              </>
            ) : isDragActive ? (
              <h3 className="text-xl md:text-2xl font-semibold text-foreground">
                Suelta las imágenes aquí
              </h3>
            ) : (
              <>
                <p className="text-sm text-muted-foreground font-medium mb-2">
                  ⬆ Comprime tu imagen en segundos
                </p>
                <div
                  className="inline-block bg-primary text-primary-foreground px-10 py-4 rounded-xl text-lg font-semibold shadow-md hover:opacity-90 hover:shadow-lg transition-all duration-200 pointer-events-none select-none"
                >
                  Sube tu imagen y comprímela en segundos
                </div>
                <p className="text-sm text-muted-foreground">
                  Reduce el tamaño en menos de 10 segundos
                </p>
              </>
            )}

            <div className="flex flex-wrap items-center justify-center gap-3 pt-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="text-green-600">✔</span> JPG</span>
              <span className="flex items-center gap-1"><span className="text-green-600">✔</span> PNG</span>
              <span className="flex items-center gap-1"><span className="text-green-600">✔</span> WebP</span>
              <span className="flex items-center gap-1"><span className="text-green-600">✔</span> Hasta {maxSizeText} por imagen</span>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Indicators - Below Dropzone */}
      {filesData.length === 0 && (
        <>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mt-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span>⚡</span>
              <span>Compresión en segundos</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span>🔒</span>
              <span>No almacenamos tus imágenes</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span>🆓</span>
              <span>100% gratis</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-2">
            ✨ Miles de imágenes comprimidas cada día
          </p>
        </>
      )}

      {/* Progress indicator during compression */}
      {isProcessing && (
        <div className="mt-6 space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Comprimiendo imágenes...</span>
            <span>{processedCount}/{totalFilesCount}</span>
          </div>
          <Progress value={compressionProgress} className="h-2" />
        </div>
      )}

      {/* Success Message & Actions */}
      {completedFiles.length > 0 && !isProcessing && (
        <div className="mt-6 text-center space-y-3">
          <p className="text-xl font-semibold text-foreground">
            🎉 {completedFiles.length === 1 ? 'Imagen optimizada con éxito' : `${completedFiles.length} imágenes optimizadas con éxito`}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {completedFiles.length > 1 && (
              <Button
                onClick={downloadAll}
                disabled={isDownloadingAll}
                size="sm"
                variant="outline"
              >
                <Package className="w-4 h-4 mr-1" />
                {isDownloadingAll ? 'Creando ZIP...' : `Descargar todas (ZIP)`}
              </Button>
            )}
            <button
              onClick={clearFiles}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          </div>
        </div>
      )}

      {/* Files Grid */}
      {filesData.length > 0 && (
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filesData.map((fileData, index) => (
              <ImagePreview
                key={`${fileData.file.name}-${index}`}
                file={fileData.file}
                compression={fileData.compression}
                isCompressing={fileData.isCompressing}
                status={fileData.status}
                error={fileData.error}
                retryCount={fileData.retryCount}
                maxRetryAttempts={maxRetryAttempts}
                onRemove={() => removeFile(index)}
                onRetry={() => retryFile(index, compressionConfig)}
                onCompress={() => compressFile(index, compressionConfig)}
                compressionConfig={compressionConfig}
                isPremium={isPremium}
              />
            ))}
          </div>
        </div>
      )}

      {/* Secondary action: compress another */}
      {completedFiles.length > 0 && !isProcessing && (
        <div className="mt-6 text-center">
          <Button
            variant="outline"
            onClick={() => {
              trackEvent('compress_again_click', {});
              clearFiles();
              document.getElementById('uploader-section')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Comprimir otra imagen
          </Button>
        </div>
      )}
    </div>
  );
});

ImageUploader.displayName = 'ImageUploader';

export default ImageUploader;
